// src/components/ChatBot.js
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { formatINR, finalPrice } from '../utils/priceFormat';

const PH = 'https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=80&q=60';
const resImg = ph => !ph ? PH : ph.startsWith('http') ? ph : `https://shop.venkatagirihandlooms.com/${ph.replace(/^\/+/,'')}`;

/* ── Quick replies ───────────────────────────────────────── */
const QUICK_REPLIES = [
  { label:'🔍 Silk Sarees',       action:'search_silk'    },
  { label:'💰 Under ₹2000',       action:'search_budget'  },
  { label:'👰 Bridal',            action:'search_bridal'  },
  { label:'🌿 Cotton Sarees',     action:'search_cotton'  },
  { label:'🚚 Shipping Info',     action:'shipping_info'  },
  { label:'🎟️ Coupon Codes',     action:'coupon_info'    },
];

/* ── Knowledge base ─────────────────────────────────────── */
const KB = {
  greet:    ['hi','hello','hey','namaste','vanakkam','good morning','good evening'],
  care:     ['care','wash','clean','iron','store','maintain','dry'],
  shipping: ['shipping','delivery','deliver','how long','free shipping','dispatch','track'],
  return:   ['return','exchange','refund','cancel','policy'],
  payment:  ['payment','pay','upi','card','cod','cash','razorpay','emi'],
  silk:     ['kanjivaram','kanchipuram','silk','pure silk','wedding silk','bridal silk'],
  cotton:   ['cotton','ikat','pochampally','kalamkari','day wear','casual'],
  size:     ['size','length','meter','yard','blouse','drape','how to wear'],
  price:    ['price','cost','rate','how much','budget','cheap','affordable'],
  zari:     ['zari','jari','gold thread','silver thread','border'],
  offer:    ['offer','discount','coupon','sale','promo','code','deal'],
  gift:     ['gift','wrap','occasion','birthday','anniversary'],
};

function getBotResponse(input, products) {
  const q = input.toLowerCase().trim();

  if (KB.greet.some(w => q.includes(w)))
    return { type:'text', text:'Namaste! 🙏 Welcome to Venkatagiri Handlooms!\n\nI can help you with:\n• Finding sarees by price or type\n• Shipping & return policy\n• Coupon codes & offers\n• Saree care tips\n\nWhat are you looking for today?' };

  if (KB.care.some(w => q.includes(w)))
    return { type:'text', text:'👗 Saree Care Guide:\n\n🧼 Silk — Dry clean only\n💧 Cotton — Hand wash, cold water\n☀️ Dry in shade, avoid sunlight\n🌡️ Iron on low with a cloth on zari\n📦 Store in muslin cloth, cool & dry place' };

  if (KB.shipping.some(w => q.includes(w)))
    return { type:'text', text:'🚚 Shipping Info:\n\n✅ Free shipping above ₹999\n📦 Delivery: 5–7 business days\n🔍 Track order from My Orders page\n📞 Help: +91 99636 86116' };

  if (KB.return.some(w => q.includes(w)))
    return { type:'text', text:'↩️ Return Policy:\n\n✅ 7-day hassle-free returns\n📸 Item must be unused & packed\n💰 Full refund to original payment\n🔄 Exchange available\n📞 WhatsApp: +91 99636 86116' };

  if (KB.payment.some(w => q.includes(w)))
    return { type:'text', text:'💳 Payment Options:\n\n📱 UPI — GPay, PhonePe, Paytm\n💳 Cards — Visa, Mastercard, RuPay\n🏦 Net Banking — all major banks\n📦 Cash on Delivery (COD)\n🔒 256-bit SSL encrypted' };

  if (KB.offer.some(w => q.includes(w)))
    return { type:'text', text:'🎟️ Active Coupon Codes:\n\n• WELCOME10 — 10% off (min ₹500)\n• SILK15 — 15% off silk (min ₹2000)\n• FLAT500 — ₹500 off (min ₹3000)\n• VKG200 — ₹200 off (min ₹1000)\n• FREESHIP — Free shipping\n• DIWALI20 — 20% off (min ₹1500)\n\nEnter at checkout!' };

  if (KB.gift.some(w => q.includes(w)))
    return { type:'text', text:'🎁 Gift Options:\n\n✅ Gift wrap available at checkout (+₹99)\n🎀 Tissue paper, ribbon & message card\n💌 Personalized note up to 150 chars\n\nPopular gifts:\n• Cotton sarees ₹800–₹2,500\n• Silk sarees ₹2,000–₹8,000\n• Bridal collection ₹5,000+' };

  if (KB.size.some(w => q.includes(w)))
    return { type:'text', text:'📏 Size Guide:\n\n🥻 Standard length: 5.5 metres\n📐 Width: 115 cm\n👗 Blouse piece included (~0.8 m)\n\nDraping styles:\n• Nivi (most common in AP)\n• Nauvari (9-yard)\n• Kaccha style (for functions)\n\nWatch draping tutorials on our YouTube!' };

  // Price with number
  if (KB.price.some(w => q.includes(w))) {
    const match = q.match(/(\d[\d,]*)/);
    if (match) {
      const budget = parseInt(match[1].replace(/,/g,''));
      const found  = products.filter(p => finalPrice(p.selling_price, p.discount_pct) <= budget).slice(0,4);
      if (found.length) return { type:'products', text:`Sarees within ₹${formatINR(budget)}:`, products: found };
      return { type:'text', text:`No sarees found under ₹${formatINR(budget)}. Our cotton range starts from ₹500!\n\nTry a higher budget or ask me to "show cotton sarees".` };
    }
    return { type:'text', text:'💰 Our Price Range:\n\n🌿 Cotton: ₹500 – ₹2,500\n🥻 Silk: ₹2,000 – ₹15,000\n✨ Bridal silk: ₹5,000 – ₹25,000\n⭐ Zari work: ₹3,000 – ₹20,000\n\nWhat is your budget? I\'ll find options!' };
  }

  // Silk search
  if (KB.silk.some(w => q.includes(w))) {
    const found = products.filter(p =>
      (p.material||'').toLowerCase().includes('silk') ||
      (p.category_name||'').toLowerCase().includes('silk') ||
      (p.tags||[]).join(' ').toLowerCase().includes('silk')
    ).slice(0,4);
    if (found.length) return { type:'products', text:'Our finest silk sarees:', products: found };
    return { type:'text', text:'Our silk collection includes Kanjivaram, pure silk, and zari work. Browse our silk collection for all options!' };
  }

  // Cotton search
  if (KB.cotton.some(w => q.includes(w))) {
    const found = products.filter(p =>
      (p.material||'').toLowerCase().includes('cotton') ||
      (p.tags||[]).join(' ').toLowerCase().includes('cotton')
    ).slice(0,4);
    if (found.length) return { type:'products', text:'Our bestselling cotton handlooms:', products: found };
    return { type:'text', text:'We have beautiful Venkatagiri cotton, Pochampally ikat, and daily wear cotton sarees. Check our cotton collection!' };
  }

  // Generic product search
  const lower = q.toLowerCase();
  const found = products.filter(p =>
    p.name?.toLowerCase().includes(lower) ||
    p.material?.toLowerCase().includes(lower) ||
    p.category_name?.toLowerCase().includes(lower) ||
    (p.tags||[]).join(' ').toLowerCase().includes(lower)
  ).slice(0,4);
  if (found.length > 0) return { type:'products', text:`Found ${found.length} saree${found.length>1?'s':''} for "${input}":`, products: found };

  return { type:'text', text:'I\'m not sure about that, but happy to help! 😊\n\nTry asking:\n• "Show silk sarees"\n• "Sarees under ₹2000"\n• "What is the return policy?"\n• "Do you have coupon codes?"\n• "Cotton sarees"' };
}

/* ── Product card in chat ────────────────────────────────── */
function ChatProduct({ p, onClick }) {
  const fp    = finalPrice(p.selling_price, p.discount_pct);
  const photo = resImg(Array.isArray(p.photos) ? p.photos[0] : (p.photo || ''));
  return (
    <div onClick={() => onClick(p.slug)}
      style={{ display:'flex', gap:10, background:'#fff', border:'1px solid var(--border)', padding:'10px 10px', cursor:'pointer', marginBottom:8, borderRadius:6, transition:'box-shadow 0.2s' }}
      onMouseEnter={e => e.currentTarget.style.boxShadow='0 3px 12px rgba(122,31,31,0.12)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow='none'}>
      <img src={photo} alt={p.name} style={{ width:52, height:66, objectFit:'cover', flexShrink:0, background:'#f7f0e6', borderRadius:4 }} onError={e => { e.target.src = PH; }} />
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:14, color:'#1a0f0f', lineHeight:1.3, marginBottom:3, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.name}</div>
        {p.material && <div style={{ fontSize:10, color:'#8a7968', fontFamily:"'Jost',sans-serif", marginBottom:4 }}>{p.material}</div>}
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:16, color:'#7a1f1f', fontWeight:600 }}>₹{formatINR(fp)}</span>
          {p.discount_pct > 0 && <span className="price-save" style={{ fontSize:9 }}>{p.discount_pct}% OFF</span>}
        </div>
      </div>
      <div style={{ color:'#c9a84c', fontSize:18, alignSelf:'center', flexShrink:0 }}>›</div>
    </div>
  );
}

/* ── Message bubble ─────────────────────────────────────── */
function Bubble({ msg, onProductClick }) {
  const isBot = msg.role === 'bot';
  return (
    <div style={{ display:'flex', flexDirection: isBot ? 'row' : 'row-reverse', gap:8, marginBottom:10, alignItems:'flex-start' }}>
      {isBot && (
        <div style={{ width:28, height:28, borderRadius:'50%', background:'linear-gradient(135deg,#7a1f1f,#5a1515)', color:'#e8c96a', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, flexShrink:0, marginTop:2 }}>🥻</div>
      )}
      <div style={{ maxWidth:'84%' }}>
        {msg.type === 'products' ? (
          <>
            <div style={{ background:'#f7f0e6', padding:'9px 13px', borderRadius:'0 10px 10px 10px', fontSize:13, fontFamily:"'Jost',sans-serif", marginBottom:8, color:'#2d1f1f' }}>{msg.text}</div>
            {msg.products.map(p => <ChatProduct key={p.id} p={p} onClick={onProductClick} />)}
          </>
        ) : (
          <div style={{ background: isBot ? '#f7f0e6' : '#7a1f1f', color: isBot ? '#2d1f1f' : '#fff', padding:'9px 13px', borderRadius: isBot ? '0 10px 10px 10px' : '10px 0 10px 10px', fontSize:13, fontFamily:"'Jost',sans-serif", lineHeight:1.65, whiteSpace:'pre-line', wordBreak:'break-word' }}>
            {msg.text}
          </div>
        )}
        <div style={{ fontSize:10, color:'#ccc', fontFamily:"'Jost',sans-serif", marginTop:3, textAlign: isBot ? 'left' : 'right' }}>{msg.time}</div>
      </div>
    </div>
  );
}

/* ── Typing indicator ───────────────────────────────────── */
function TypingIndicator() {
  return (
    <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:10 }}>
      <div style={{ width:28, height:28, borderRadius:'50%', background:'linear-gradient(135deg,#7a1f1f,#5a1515)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13 }}>🥻</div>
      <div style={{ background:'#f7f0e6', padding:'10px 14px', borderRadius:'0 10px 10px 10px', display:'flex', gap:4, alignItems:'center' }}>
        {[0,1,2].map(i => (
          <span key={i} style={{ width:7, height:7, borderRadius:'50%', background:'#7a1f1f', display:'block', animation:`chatBounce 0.8s ${i*0.2}s infinite` }} />
        ))}
      </div>
    </div>
  );
}

/* ── Main export ────────────────────────────────────────── */
export default function ChatBot() {
  const navigate   = useNavigate();
  const [open,     setOpen]     = useState(false);
  const [msgs,     setMsgs]     = useState([]);
  const [input,    setInput]    = useState('');
  const [typing,   setTyping]   = useState(false);
  const [products, setProducts] = useState([]);
  const [unread,   setUnread]   = useState(0);
  const [greeted,  setGreeted]  = useState(false);
  const bottomRef  = useRef(null);
  const inputRef   = useRef(null);

  // Load products once
  useEffect(() => {
    api.get('/products?limit=60').then(r => setProducts(r.data?.products || [])).catch(() => {});
  }, []);

  // Tease badge after 5s
  useEffect(() => {
    if (greeted || open) return;
    const t = setTimeout(() => { setUnread(1); setGreeted(true); }, 5000);
    return () => clearTimeout(t);
  }, [greeted, open]);

  // Scroll to bottom
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior:'smooth' }); }, [msgs, typing]);

  // Focus + greet on open
  useEffect(() => {
    if (!open) return;
    setUnread(0);
    setTimeout(() => inputRef.current?.focus(), 150);
    if (msgs.length === 0) {
      setMsgs([{
        role:'bot', type:'text', time: nowTime(),
        text:'Namaste! 🙏 I\'m your Venkatagiri Handlooms assistant.\n\nAsk me about sarees, prices, shipping, or use a quick reply below!'
      }]);
    }
  }, [open]);

  const nowTime = () => new Date().toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit' });

  const addBot = useCallback(resp => {
    setMsgs(m => [...m, { role:'bot', type: resp.type||'text', text:resp.text, products:resp.products, time:nowTime() }]);
  }, []);

  const sendMessage = async (text) => {
    const q = (text || input).trim();
    if (!q) return;
    setInput('');
    setMsgs(m => [...m, { role:'user', type:'text', text:q, time:nowTime() }]);
    setTyping(true);
    try {
      await new Promise(r => setTimeout(r, 600 + Math.random()*400));
      const resp = getBotResponse(q, products);
      addBot(resp);
    } catch (e) {
      addBot({ type:'text', text:'Sorry, something went wrong. Please try again!' });
    } finally {
      setTyping(false);
    }
  };

  const handleQuick = (action) => {
    const map = {
      search_silk:   'Show silk sarees',
      search_budget: 'Sarees under ₹2000',
      search_bridal: 'Bridal kanjivaram silk sarees',
      search_cotton: 'Show cotton sarees',
      shipping_info: 'Shipping and delivery info',
      coupon_info:   'What coupon codes are available?',
    };
    if (map[action]) sendMessage(map[action]);
  };

  const clearChat = () => {
    setMsgs([{
      role:'bot', type:'text', time:nowTime(),
      text:'Chat cleared! 🌿 How can I help you?\n\nAsk me about sarees, prices, coupons, or shipping!'
    }]);
  };

  const handleProductClick = slug => { setOpen(false); navigate(`/product/${slug}`); };

  return (
    <>
      {/* FAB */}
      <button className="chatbot-fab" onClick={() => setOpen(o => !o)} aria-label="Saree Assistant">
        <span style={{ fontSize: open ? 20 : 24, transition:'font-size 0.2s' }}>{open ? '✕' : '🤖'}</span>
        {!open && unread > 0 && (
          <span style={{ position:'absolute', top:-2, right:-2, background:'#f59e0b', color:'#fff', fontSize:10, fontWeight:700, width:18, height:18, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center' }}>{unread}</span>
        )}
      </button>

      {/* Chat window */}
      {open && (
        <div className="chatbot-window">

          {/* Header */}
          <div style={{ background:'linear-gradient(135deg,#7a1f1f,#5a1515)', padding:'12px 16px', display:'flex', alignItems:'center', gap:10, flexShrink:0 }}>
            <div style={{ width:36, height:36, borderRadius:'50%', background:'rgba(255,255,255,0.15)', border:'2px solid rgba(201,168,76,0.4)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:17, flexShrink:0 }}>🥻</div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontFamily:"'Playfair Display',serif", fontSize:15, color:'#fff', fontWeight:700 }}>Saree Assistant</div>
              <div style={{ fontSize:10, color:'rgba(255,255,255,0.65)', fontFamily:"'Jost',sans-serif", display:'flex', alignItems:'center', gap:4 }}>
                <span style={{ width:6, height:6, borderRadius:'50%', background:'#4ade80', display:'inline-block' }} />
                Online · Replies instantly
              </div>
            </div>
            {/* Clear chat button */}
            <button onClick={clearChat} title="Clear chat"
              style={{ background:'rgba(255,255,255,0.12)', border:'1px solid rgba(255,255,255,0.2)', color:'rgba(255,255,255,0.8)', width:32, height:32, borderRadius:6, cursor:'pointer', fontSize:14, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              🗑️
            </button>
            {/* WhatsApp button */}
            <a href="https://wa.me/919963686116" target="_blank" rel="noopener noreferrer"
              style={{ background:'#25D366', color:'#fff', width:32, height:32, borderRadius:6, display:'flex', alignItems:'center', justifyContent:'center', textDecoration:'none', fontSize:16, flexShrink:0 }} title="WhatsApp">
              💬
            </a>
          </div>

          {/* Messages */}
          <div className="chatbot-messages">
            {msgs.map((msg, i) => <Bubble key={i} msg={msg} onProductClick={handleProductClick} />)}
            {typing && <TypingIndicator />}
            <div ref={bottomRef} />
          </div>

          {/* Quick replies — show when ≤1 bot message */}
          {msgs.filter(m => m.role==='bot').length <= 1 && (
            <div style={{ padding:'0 10px 8px', display:'flex', gap:6, flexWrap:'wrap', borderTop:'1px solid #f0e8e0', paddingTop:8 }}>
              {QUICK_REPLIES.map(qr => (
                <button key={qr.action} onClick={() => handleQuick(qr.action)}
                  style={{ padding:'5px 11px', border:'1px solid var(--border)', background:'#fdf8f0', fontFamily:"'Jost',sans-serif", fontSize:11, cursor:'pointer', borderRadius:14, color:'#5a3a3a', transition:'all 0.18s', whiteSpace:'nowrap' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor='#7a1f1f'; e.currentTarget.style.color='#7a1f1f'; e.currentTarget.style.background='#fdf3f0'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor='var(--border)'; e.currentTarget.style.color='#5a3a3a'; e.currentTarget.style.background='#fdf8f0'; }}>
                  {qr.label}
                </button>
              ))}
            </div>
          )}

          {/* Input bar */}
          <div style={{ padding:'10px 12px', borderTop:'1px solid var(--border)', display:'flex', gap:8, background:'#fdf8f0', flexShrink:0 }}>
            <input ref={inputRef} value={input} onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
              placeholder="Ask about sarees, prices, care…"
              style={{ flex:1, padding:'9px 14px', border:'1.5px solid var(--border)', background:'#fff', fontSize:13, outline:'none', borderRadius:20, fontFamily:"'Jost',sans-serif", minWidth:0 }} />
            <button onClick={() => sendMessage()}
              style={{ width:38, height:38, borderRadius:'50%', background: input.trim() ? '#7a1f1f' : '#e8ddd0', color: input.trim() ? '#fff' : '#aaa', border:'none', cursor: input.trim() ? 'pointer' : 'default', display:'flex', alignItems:'center', justifyContent:'center', fontSize:15, flexShrink:0, transition:'background 0.2s' }}>
              ➤
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes chatBounce {
          0%,80%,100% { transform:translateY(0);   }
          40%          { transform:translateY(-6px); }
        }
      `}</style>
    </>
  );
}
